import { injectStructuredData } from "./structuredData";

/**
 * Church address and contact information
 */
const CHURCH_INFO = {
  name: "PIWC Leicester - The Church of Pentecost",
  alternateName: "PIWC Leicester",
  url: "https://piwcleicester.org",
  logo: "https://piwcleicester.org/LEICESTERPIWC.webp",
  address: {
    streetAddress: "Church Premises",
    addressLocality: "Leicester",
    addressRegion: "Leicestershire",
    postalCode: "LE1",
    addressCountry: "GB",
  },
  telephone: "+44",
  email: "piwcleicester@copuk.org",
  sameAs: [
    "https://www.youtube.com/@leicesterpiwc",
    // Add other social media links here
  ],
};

/**
 * Service times for structured data
 */
const SERVICE_TIMES = [
  {
    dayOfWeek: "Sunday",
    opens: "10:00",
    closes: "12:30",
    description: "Sunday Service",
  },
  {
    dayOfWeek: "Monday",
    opens: "19:00",
    closes: "20:00",
    description: "Youth Meetings",
  },
  {
    dayOfWeek: "Tuesday",
    opens: "06:00",
    closes: "07:00",
    description: "Dawn Prayers",
  },
  {
    dayOfWeek: "Thursday",
    opens: "11:00",
    closes: "12:30",
    description: "Prayer Clinic",
  },
  {
    dayOfWeek: "Friday",
    opens: "19:30",
    closes: "20:30",
    description: "Transformation Friday",
  },
];

/**
 * Generate Organization + PlaceOfWorship structured data
 */
export function generateChurchStructuredData() {
  return {
    "@context": "https://schema.org",
    "@type": ["Organization", "PlaceOfWorship"],
    name: CHURCH_INFO.name,
    alternateName: CHURCH_INFO.alternateName,
    url: CHURCH_INFO.url,
    logo: {
      "@type": "ImageObject",
      url: CHURCH_INFO.logo,
    },
    address: {
      "@type": "PostalAddress",
      streetAddress: CHURCH_INFO.address.streetAddress,
      addressLocality: CHURCH_INFO.address.addressLocality,
      addressRegion: CHURCH_INFO.address.addressRegion,
      postalCode: CHURCH_INFO.address.postalCode,
      addressCountry: CHURCH_INFO.address.addressCountry,
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: CHURCH_INFO.telephone,
      email: CHURCH_INFO.email,
      contactType: "General Inquiries",
    },
    sameAs: CHURCH_INFO.sameAs,
    openingHoursSpecification: SERVICE_TIMES.map((service) => ({
      "@type": "OpeningHoursSpecification",
      dayOfWeek: service.dayOfWeek,
      opens: service.opens,
      closes: service.closes,
      description: service.description,
    })),
  };
}

/**
 * Generate Event structured data
 */
export function generateEventStructuredData(event: {
  name: string;
  description: string;
  startDate: string;
  endDate?: string;
  location: string;
  image?: string;
  url?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Event",
    name: event.name,
    description: event.description,
    startDate: event.startDate,
    ...(event.endDate && { endDate: event.endDate }),
    location: {
      "@type": "Place",
      name: event.location,
      address: {
        "@type": "PostalAddress",
        addressLocality: CHURCH_INFO.address.addressLocality,
        addressRegion: CHURCH_INFO.address.addressRegion,
        addressCountry: CHURCH_INFO.address.addressCountry,
      },
    },
    ...(event.image && {
      image: {
        "@type": "ImageObject",
        url: event.image,
      },
    }),
    ...(event.url && { url: event.url }),
    organizer: {
      "@type": "Organization",
      name: CHURCH_INFO.name,
      url: CHURCH_INFO.url,
    },
  };
}

/**
 * Generate BreadcrumbList structured data
 */
export function generateBreadcrumbStructuredData(items: Array<{ name: string; url: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

/**
 * Inject church structured data into page
 */
export function injectChurchSchema() {
  const schema = generateChurchStructuredData();
  injectStructuredData(schema, "church-schema");
}

